import { Routes } from '@angular/router'
import {
  _mapToObject,
  _numberEnumEntries,
  _numberEnumValues,
  NumberEnum,
} from '@naturalcycles/js-lib'
import {
  BreakpointAction,
  ChoiceFunction,
  ConceiveTimingBreakpointOption,
  GoalBreakpointOption,
  QuizDataInput,
  shortQuizGoalOptions,
} from '@naturalcycles/shared'
import { allQuizSections } from '@src/app/constants/quiz'
import {
  addPageAtStartOfSection,
  addPageInSectionAfterPage,
  filterPagesFromSection,
} from '@src/app/core/util/quiz.util.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { DefaultSections } from '@src/app/shared/typings/enum/quiz.js'

export const ab392Routes: Routes = [
  {
    path: Page.introChapter,
    loadComponent: () =>
      import('./intro-chapter/intro-chapter.page.component').then(m => m.IntroChapterPage),
  },
  {
    path: Page.goalChapter,
    loadComponent: () =>
      import('./goal-chapter/goal-chapter.page.component').then(m => m.GoalChapterPage),
  },
  {
    path: Page.cycleChapter,
    loadComponent: () =>
      import('./cycle-chapter/cycle-chapter.page.component').then(m => m.CycleChapterPage),
  },
  {
    path: Page.changesChapter,
    loadComponent: () =>
      import('./changes-chapter/changes-chapter.page.component').then(m => m.ChangesChapterPage),
  },
  {
    path: Page.routineChapter,
    loadComponent: () =>
      import('./routine-chapter/routine-chapter.page.component').then(m => m.RoutineChapterPage),
  },
]

export const ab392QuizPages = ab392Routes.map(r => r.path)

export function getAb392ShortGoalBreakpointAction(
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
  goal: GoalBreakpointOption,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    flow = existingBreakpointAction?.[goal]?.(flow, data) || flow

    // Undo changes from other goal options, in case the user changed their mind
    flow = filterPagesFromSection(flow, [Page.introChapter], DefaultSections.intro)

    return flow
  }
}

export function getAb392DefaultGoalTransformFunction(
  existingChoiceFunction: ChoiceFunction<Page, QuizDataInput> | undefined,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    flow = existingChoiceFunction?.(flow, data) || flow
    flow = filterPagesFromSection(flow, [Page.introChapter], DefaultSections.intro)
    flow = filterPagesFromSection(flow, [Page.goalChapter], DefaultSections.goal)

    flow = addPageInSectionAfterPage(flow, Page.introChapter, Page.privacy, DefaultSections.intro)
    flow = addPageAtStartOfSection(flow, Page.goalChapter, DefaultSections.goal)
    return flow
  }
}

export function getAb392PlanGoalTransformFunction(
  existingChoiceFunction: ChoiceFunction<Page, QuizDataInput> | undefined,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    flow = existingChoiceFunction?.(flow, data) || flow
    flow = filterPagesFromSection(flow, [Page.introChapter], DefaultSections.intro)
    flow = filterPagesFromSection(flow, [Page.goalChapter], DefaultSections.goal)

    flow = addPageInSectionAfterPage(flow, Page.introChapter, Page.privacy, DefaultSections.intro)
    flow = addPageInSectionAfterPage(
      flow,
      Page.goalChapter,
      Page.plannedPregnancyTiming,
      DefaultSections.goal,
    )
    return flow
  }
}

export function getAb392PlanConceiveTimingTransformFunction(
  existingChoiceFunction: ChoiceFunction<Page, QuizDataInput> | undefined,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    flow = existingChoiceFunction?.(flow, data) || flow
    flow = filterPagesFromSection(
      flow,
      [Page.goalChapter, Page.learnAboutSex],
      DefaultSections.goal,
    )
    flow = filterPagesFromSection(flow, [Page.cycleChapter], DefaultSections.cycle)

    flow = addPageInSectionAfterPage(
      flow,
      Page.goalChapter,
      Page.plannedPregnancyTiming,
      DefaultSections.goal,
    )
    flow = addPageAtStartOfSection(flow, Page.cycleChapter, DefaultSections.cycle)
    return flow
  }
}

export function getAb392PlanConceiveTimingBreakpointAction(
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
): BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> {
  return {
    ..._mapToObject(_numberEnumEntries(ConceiveTimingBreakpointOption), ([_, conceiveTiming]) => [
      conceiveTiming,
      getAb392PlanConceiveTimingTransformFunction(existingBreakpointAction?.[conceiveTiming]),
    ]),
  }
}
export function getAb392GoalBreakpointAction(
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
): BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> {
  return {
    ..._mapToObject(_numberEnumEntries(GoalBreakpointOption), ([_, goal]) => [
      goal,
      getAb392DefaultGoalTransformFunction(existingBreakpointAction?.[goal]),
    ]),
    ..._mapToObject(shortQuizGoalOptions, goal => [
      goal,
      getAb392ShortGoalBreakpointAction(existingBreakpointAction, goal),
    ]),
    [GoalBreakpointOption.plan]: getAb392PlanGoalTransformFunction(
      existingBreakpointAction?.[GoalBreakpointOption.plan],
    ),
  }
}

export function getAb392DefaultChaptersBreakpointWrapper<BreakpointOption extends number>(
  existingBreakpointAction: BreakpointAction<BreakpointOption, Page, QuizDataInput> | undefined,
  breakpointOption: NumberEnum,
  section: DefaultSections,
  page: Page,
): BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> {
  return _mapToObject(
    _numberEnumValues(breakpointOption),
    value =>
      [
        value,
        (flow, data) => {
          flow = existingBreakpointAction?.[value as BreakpointOption]?.(flow, data) || flow
          flow = filterPagesFromSection(flow, [page], section)
          if (flow[allQuizSections[section]]?.length) {
            flow = addPageAtStartOfSection(flow, page, section)
          }
          return flow
        },
      ] as const,
  )
}
export const imageLinkMap: Record<DefaultSections, Record<'mobile' | 'desktop', string>> = {
  [DefaultSections.intro]: {
    mobile: 'https://www.datocms-assets.com/21281/1738667325-chapter-37.png',
    desktop: 'https://www.datocms-assets.com/21281/1738242403-section-1-desktop-1.jpg',
  },
  [DefaultSections.goal]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974820-goal-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242452-section-2-desktop.jpg',
  },
  [DefaultSections.cycle]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974819-cycle-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242516-section-3-desktop.jpg',
  },
  [DefaultSections.cycleChanges]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974819-changes-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242535-section-3-desktop-1.jpg',
  },
  [DefaultSections.routine]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974819-routine-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242568-section-3-desktop-2.jpg',
  },
}
